import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: '1. Información que Recopilamos',
    text: 'Recopilamos información personal que nos proporcionas directamente, como tu nombre, dirección de correo electrónico, dirección de envío y talla seleccionada al realizar una compra o solicitar asesoría personalizada.'
  }, {
    heading: '2. Uso de la Información',
    text: 'Utilizamos tu información para procesar pedidos, enviar tu faja colombiana, coordinar tu asesoría personalizada, enviar el eBook digital y comunicarnos contigo sobre tu compra. No vendemos ni compartimos tu información personal con terceros para fines de marketing.'
  }, {
    heading: '3. Protección de Datos',
    text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
  }, {
    heading: '4. Cookies',
    text: 'Nuestro sitio web puede utilizar cookies para mejorar tu experiencia de navegación. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar la funcionalidad del sitio.'
  }, {
    heading: '5. Tus Derechos',
    text: 'Tienes derecho a acceder, corregir o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes contactarnos a través de nuestros canales de atención.'
  }, {
    heading: '6. Cambios a Esta Política',
    text: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página con la fecha de última actualización.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: '1. Aceptación de los Términos',
    text: 'Al acceder y utilizar este sitio web y realizar una compra, aceptas estar sujeto a estos términos y condiciones. Si no estás de acuerdo con alguno de estos términos, te pedimos que no utilices nuestros servicios.'
  }, {
    heading: '2. Productos y Servicios',
    text: 'Ofrecemos un paquete que incluye una faja colombiana original (tallas 2XL a 5XL), un eBook digital con guía de uso y cuidado, y una asesoría personalizada 1:1. Las imágenes de los productos son representativas y pueden variar ligeramente del producto final.'
  }, {
    heading: '3. Precios y Pagos',
    text: 'Los precios están expresados en dólares estadounidenses (USD). Nos reservamos el derecho de modificar los precios y las ofertas promocionales en cualquier momento sin previo aviso. El pago debe realizarse en su totalidad al momento de la compra.'
  }, {
    heading: '4. Envíos',
    text: 'Realizamos envíos a todo Estados Unidos sin costo adicional. Los tiempos de entrega pueden variar según la ubicación. No nos hacemos responsables por retrasos causados por el servicio de mensajería o aduanas.'
  }, {
    heading: '5. Política de Devoluciones',
    text: 'Si no estás satisfecha con tu faja, puedes solicitar un cambio de talla dentro de los primeros 15 días después de recibir el producto, siempre que se encuentre en su empaque original y sin uso. El eBook digital y la asesoría no son reembolsables una vez entregados.'
  }, {
    heading: '6. Propiedad Intelectual',
    text: 'Todo el contenido de este sitio web, incluyendo textos, imágenes, logotipos y el eBook digital, está protegido por derechos de autor. Queda prohibida su reproducción, distribución o uso sin autorización previa.'
  }, {
    heading: '7. Limitación de Responsabilidad',
    text: 'No garantizamos resultados específicos con el uso de nuestros productos. Los resultados pueden variar según cada persona. Nuestros productos no sustituyen el consejo médico profesional.'
  }]
};
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-29">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-30" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      y: 30,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className="relative bg-dark-800 border border-gold-dark/30 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-31">
        {/* Header */}
        <div className="sticky top-0 bg-dark-800 border-b border-gold-dark/20 px-6 py-4 flex items-center justify-between z-10" data-id="element-32">
          <h2 className="text-xl font-serif font-bold text-white" data-id="element-33">
            {content.title}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-dark-700 transition-colors" data-id="element-34">
            <X className="w-5 h-5 text-gray-400" data-id="element-35" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-70px)] space-y-6" data-id="element-36">
          {content.sections.map((section, index) => <div key={index} data-id="element-37">
              <h3 className="text-gold-DEFAULT font-bold mb-2" data-id="element-38">
                {section.heading}
              </h3>
              <p className="text-gray-400 leading-relaxed text-sm" data-id="element-39">
                {section.text}
              </p>
            </div>)}

          <div className="pt-4 border-t border-gray-800" data-id="element-40">
            <p className="text-gray-600 text-xs" data-id="element-41">
              Última actualización: Febrero 2026
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-8 bg-dark-900 border-t border-gray-800/50" data-id="element-42">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-43">
          <p className="text-gray-600 text-sm" data-id="element-44">
            © 2024 Fajas Colombianas Originales. Todos los derechos reservados.
          </p>

          <div className="flex items-center gap-6" data-id="element-45">
            <button onClick={() => setActiveModal('privacy')} className="text-gray-500 text-sm hover:text-gold-DEFAULT transition-colors" data-id="element-46">
              Política de Privacidad
            </button>
            <span className="text-gray-800" data-id="element-47">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-gray-500 text-sm hover:text-gold-DEFAULT transition-colors" data-id="element-48">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-49">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-50" />}
      </AnimatePresence>
    </>;
}